import * as THREE from 'three';
import type { Checkpoint, World } from '../../shared/types.js';
import { distToSegment2, headingFromDirection } from '../../shared/geom.js';
import { Heightfield } from '../../shared/heightfield.js';
import { DeckIndex } from '../../shared/decks.js';
import { ROAD_SPECS } from '../../shared/osm/classify.js';
import { gradedRoadHeight } from './worldMesh.js';

/**
 * The race furniture: a start line, a gate at each checkpoint, a chequered finish, and a lit
 * guide line painted down the road.
 *
 * The guide line is what answers "where do I go" — a satnav route drawn on the tarmac, with
 * chevrons flowing along it towards the next gate. There used to be a floating arrow in
 * front of the car as well, and it went through three shapes before being cut: an arrow in
 * mid-air can only ever say *that* way, while a line on the road says *this street*, and
 * says it far enough ahead to brake for. Once the line worked, the arrow was something to
 * see past.
 *
 * Gates are built as one group per checkpoint rather than merged, because their colour
 * changes as you pass them and swapping a material on twenty groups costs nothing.
 *
 * Everything here is drawn to the width of the *street*, not to the gate's `radius`. The
 * radius is how close counts as through, and it is deliberately generous — at least 13 m
 * either side, 20 m at the finish — so drawn at that size a gate on an old-town street
 * stood its posts inside the buildings and laid its band across the pavement, the
 * courtyard and somebody's roof. See `furnitureHalfWidth`.
 */

export interface RouteMeshes {
  group: THREE.Group;
  /** Recolour gates so the one you need next is the one that glows. */
  setNextCheckpoint(index: number): void;
  /** Flow the chevrons along the guide line. */
  update(dt: number): void;
  dispose(): void;
}

const PENDING_COLOR = 0x39c6ff;
const NEXT_COLOR = 0xffd84d;
const PASSED_COLOR = 0x33513f;

/** Guide line: metres wide, metres of road per repeat of the chevron texture. */
const GUIDE_WIDTH = 2.2;
const GUIDE_PERIOD = 9;
/**
 * Clearance above the carriageway, metres.
 *
 * Above `maxRoadY` (0.2 m, the top of the road-class ladder) so it clears the tarmac
 * whatever the street, and low enough that it still reads as paint rather than as a ribbon
 * floating over the road. Measured *from the graded road height*, not from raw terrain —
 * see `RESAMPLE_METRES` for what that distinction cost the first time.
 */
const GUIDE_Y = 0.3;
/**
 * How finely the route is resampled before the ribbon is built.
 *
 * The route's own path is a waypoint list, not a drawing: Kraków's Old Town route is 32
 * points over 2.3 km — chords of about seventy metres. Drawn as-is on real relief, the
 * ribbon only touches the ground at its waypoints and every chord between them cuts
 * straight through whatever the ground does in the middle. On the flat fixture it looked
 * perfect; on the Old Town it was *underground for most of its length*, which is exactly
 * how it looked: a route line that simply was not there.
 *
 * Four metres is a little under the road-mesh's own densification, so the ribbon is at
 * least as faithful to the ground as the tarmac it is painted on, and 2.3 km of route comes
 * to about 1200 vertices.
 */
const RESAMPLE_METRES = 4;

/**
 * How far past the kerb the furniture reaches, metres: enough that a post stands on the
 * pavement rather than in the gutter, not so much that it reaches the building line.
 */
const KERB_MARGIN = 0.8;
/** Never narrower than this either side, so a gate on a service alley is still a gate. */
const MIN_HALF_WIDTH = 3.5;

/** The finish gantry: post height and the chequered board across the top, metres. */
const GANTRY_HEIGHT = 6.4;
const GANTRY_BOARD = 1.5;
/** Size of one chequer square, so the pattern is the same scale on every street. */
const CHEQUER_METRES = 0.75;
/**
 * The beacon over the finish: how tall, and how wide at the bottom.
 *
 * It replaces two plain white pillars whose job was to be seen over the rooftops from a
 * street away. Ten metres was never going to do that in a city of six-storey blocks, and
 * once the furniture was sized to the street they would have been standing on the
 * pavement, where a pillar is a lamp post. A column of light is unmistakable from anywhere
 * and costs eight triangles; it fades upwards so it reads as light, not as a pole.
 */
const BEACON_HEIGHT = 90;
const BEACON_RADIUS = 1.6;
const FINISH_GREEN = 0x8fffb0;

export function buildRouteMeshes(world: World): RouteMeshes {
  const group = new THREE.Group();
  group.name = 'route';
  const disposables: { dispose(): void }[] = [];

  const route = world.route;
  // Race furniture used to sit at a flat y regardless of the ground under it, which is
  // invisible on the flat fixtures and wrong everywhere with a hill in it. On flat terrain
  // `heightAt` returns zero and every position below is exactly what it was.
  const terrain = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
  // The height the *carriageway* is drawn at, not the raw field: the road is eased along
  // itself and can sit up to 0.15 m above bare ground, which is more clearance than anything
  // painted on it has to give away. `gradedRoadHeight` is the mesh builder's own function,
  // so these cannot drift apart. On flat terrain it returns the raw height and every
  // position below is what it was.
  //
  // And on a bridge, the deck. Which deck — or whether the street under it — is decided the
  // way the physics decides it: by walking the route and asking at each step from the height
  // the last step was at, so a route that passes under a viaduct keeps its arrows on the
  // street and one that climbs onto it keeps them on top.
  const decks = DeckIndex.build(world, terrain);
  const along = decks.isEmpty ? null : routeHeights(route.path, terrain, decks);
  const surfaceAt = (x: number, z: number, dx = 0, dz = 0) => {
    const ground = gradedRoadHeight(terrain, x, z, dx, dz);
    if (!along) return ground;
    return decks.sample(x, z, along(x, z))?.y ?? ground;
  };

  // Shared geometry: every gate is the same box, scaled per checkpoint.
  const postGeo = new THREE.BoxGeometry(1, 1, 1);
  const stripGeo = new THREE.PlaneGeometry(1, 1);
  stripGeo.rotateX(-Math.PI / 2);
  disposables.push(postGeo, stripGeo);

  interface Gate {
    materials: THREE.MeshBasicMaterial[];
  }
  const gates: Gate[] = [];

  for (const cp of route.checkpoints) {
    const { gate, materials } = buildGate(
      cp,
      furnitureHalfWidth(world, cp),
      postGeo,
      stripGeo,
      PENDING_COLOR,
      surfaceAt(cp.x, cp.z, cp.dx, cp.dz),
    );
    group.add(gate);
    gates.push({ materials });
    disposables.push(...materials);
  }

  // Start: a plain green band across the road.
  const start = buildBand(
    route.start,
    furnitureHalfWidth(world, route.start),
    stripGeo,
    0x46d16a,
    0.4,
    surfaceAt(route.start.x, route.start.z, route.start.dx, route.start.dz),
  );
  group.add(start.mesh);
  disposables.push(start.material);

  // Finish: a chequered band across the road, and a gantry over it carrying the same
  // pattern, so the line reads from a car's height and not only from the air — a flat band
  // seen from thirty metres back at bumper height is a thin white stripe.
  const finish = route.finish;
  const finishHalf = furnitureHalfWidth(world, finish);
  const finishY = surfaceAt(finish.x, finish.z, finish.dx, finish.dz);
  const bandDepth = 3;
  const bandTexture = makeCheckerTexture(finishHalf * 2, bandDepth);
  const finishMat = new THREE.MeshBasicMaterial({
    map: bandTexture,
    transparent: true,
    depthWrite: false,
  });
  const finishMesh = new THREE.Mesh(stripGeo, finishMat);
  finishMesh.position.set(finish.x, finishY + 0.24, finish.z);
  finishMesh.rotation.y = headingFromDirection(finish.dx, finish.dz);
  finishMesh.scale.set(finishHalf * 2, 1, bandDepth);
  finishMesh.renderOrder = 3;
  group.add(finishMesh);
  disposables.push(finishMat, bandTexture);

  const gantry = new THREE.Group();
  gantry.position.set(finish.x, finishY, finish.z);
  gantry.rotation.y = headingFromDirection(finish.dx, finish.dz);
  // Unlit, like every other piece of race furniture: it has to read at midnight under a
  // storm exactly as well as at noon, and a lit post goes as dark as the street around it.
  const postMat = new THREE.MeshBasicMaterial({ color: 0xe9ecef });
  for (const side of [-1, 1]) {
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.set(side * finishHalf, GANTRY_HEIGHT / 2, 0);
    post.scale.set(0.45, GANTRY_HEIGHT, 0.45);
    gantry.add(post);
  }
  const boardTexture = makeCheckerTexture(finishHalf * 2, GANTRY_BOARD);
  const boardMat = new THREE.MeshBasicMaterial({ map: boardTexture });
  const board = new THREE.Mesh(postGeo, boardMat);
  board.position.set(0, GANTRY_HEIGHT - GANTRY_BOARD / 2, 0);
  board.scale.set(finishHalf * 2 + 0.45, GANTRY_BOARD, 0.3);
  gantry.add(board);
  group.add(gantry);
  disposables.push(postMat, boardMat, boardTexture);

  const beacon = buildBeacon(FINISH_GREEN);
  beacon.mesh.position.set(finish.x, finishY + GANTRY_HEIGHT, finish.z);
  group.add(beacon.mesh);
  disposables.push(beacon.geometry, beacon.material);

  // --- the guide line -------------------------------------------------------
  const guideTexture = makeChevronTexture();
  const guideGeo = buildGuideGeometry(route.path, surfaceAt);
  const guideMat = new THREE.MeshBasicMaterial({
    map: guideTexture,
    transparent: true,
    // 0.7 before the tone curve (§80). ACES lifts a dim additive amber on dark tarmac more
    // than anything else in the frame, and at 0.7 the line went from a guide to a stripe.
    opacity: 0.5,
    // Drawn straight to the canvas on the low setting, the curve would be applied inside this
    // material and the blend would then add it in display space, which turns amber to a grey
    // green. Exempt, it adds what it always added. On high the post chain grades it anyway.
    toneMapped: false,
    depthWrite: false,
    // Additive so it glows on a night street instead of turning it grey, and so it never
    // hides the tarmac it is painted on.
    blending: THREE.AdditiveBlending,
    // Not optional. The ribbon's triangles wind the way they wind because of which side of
    // the tangent each vertex went on, and with backface culling on they came out facing
    // *down* — a perfectly correct route line, drawn under the road, invisible from every
    // camera in the game. Two-sided also covers looking at it edge-on over a crest.
    side: THREE.DoubleSide,
  });
  const guide = new THREE.Mesh(guideGeo, guideMat);
  guide.renderOrder = 2;
  guide.frustumCulled = false;
  group.add(guide);
  disposables.push(guideGeo, guideMat, guideTexture);

  let flow = 0;

  const setNextCheckpoint = (index: number) => {
    for (let i = 0; i < gates.length; i++) {
      const color = i < index ? PASSED_COLOR : i === index ? NEXT_COLOR : PENDING_COLOR;
      for (const material of gates[i].materials) material.color.setHex(color, THREE.SRGBColorSpace);
    }
    // The guide line goes green for the last leg, matching the finish band.
    guideMat.color.setHex(index >= gates.length ? FINISH_GREEN : 0xffffff, THREE.SRGBColorSpace);
  };
  setNextCheckpoint(0);

  const update = (dt: number) => {
    // The chevrons crawl towards the gate at a fixed metres-per-second, which is what makes
    // a static line read as a direction rather than as a stripe.
    flow = (flow - dt * 0.55) % 1;
    guideTexture.offset.y = flow;
  };

  return {
    group,
    setNextCheckpoint,
    update,
    dispose() {
      for (const d of disposables) d.dispose();
      group.clear();
    },
  };
}

function buildGate(
  cp: Checkpoint,
  half: number,
  postGeo: THREE.BoxGeometry,
  stripGeo: THREE.PlaneGeometry,
  color: number,
  y: number,
): { gate: THREE.Group; materials: THREE.MeshBasicMaterial[] } {
  const gate = new THREE.Group();
  gate.position.set(cp.x, y, cp.z);
  // Local X ends up along the gate, local -Z along the direction of travel.
  gate.rotation.y = headingFromDirection(cp.dx, cp.dz);

  const postMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.92 });
  const bannerMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.2,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const stripMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
  });

  for (const side of [-1, 1]) {
    const post = new THREE.Mesh(postGeo, postMat);
    post.position.set(side * half, 3, 0);
    post.scale.set(0.55, 6, 0.55);
    gate.add(post);
  }

  const banner = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), bannerMat);
  banner.position.set(0, 3.2, 0);
  banner.scale.set(half * 2, 6, 1);
  banner.renderOrder = 4;
  gate.add(banner);

  const strip = new THREE.Mesh(stripGeo, stripMat);
  strip.position.set(0, 0.22, 0);
  strip.scale.set(half * 2, 1, 2.4);
  strip.renderOrder = 3;
  gate.add(strip);

  return { gate, materials: [postMat, bannerMat, stripMat] };
}

function buildBand(
  cp: Checkpoint,
  half: number,
  stripGeo: THREE.PlaneGeometry,
  color: number,
  opacity: number,
  y: number,
): { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial } {
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(stripGeo, material);
  mesh.position.set(cp.x, y + 0.23, cp.z);
  mesh.rotation.y = headingFromDirection(cp.dx, cp.dz);
  mesh.scale.set(half * 2, 1, 4);
  mesh.renderOrder = 3;
  return { mesh, material };
}

/**
 * Half the width a gate, band or gantry is drawn across: the street's, not the gate's.
 *
 * The street is whichever drivable road passes closest to the gate's centre, which on a
 * route is the one the gate stands on — the route is built along road centrelines, so the
 * distance is a rounding error. At a junction it can be the crossing street instead, which
 * is fine: either is a street the furniture fits.
 *
 * Capped at the radius, so a gate never draws wider than the area that counts it, and the
 * fallback for a world with no roads near the gate at all is the radius, which is what
 * every gate was before.
 */
export function furnitureHalfWidth(world: World, cp: Checkpoint): number {
  let best = Infinity;
  let width = 0;
  for (const road of world.roads) {
    if (!ROAD_SPECS[road.cls].drivable) continue;
    const pts = road.pts;
    for (let i = 2; i + 1 < pts.length; i += 2) {
      const { d2 } = distToSegment2(cp.x, cp.z, pts[i - 2], pts[i - 1], pts[i], pts[i + 1]);
      if (d2 < best) {
        best = d2;
        width = road.width;
      }
    }
  }
  if (width === 0) return cp.radius;
  return Math.min(cp.radius, Math.max(MIN_HALF_WIDTH, width / 2 + KERB_MARGIN));
}

/**
 * A column of light standing on the finish gantry.
 *
 * An open cylinder whose vertex colour runs from the finish green at the foot to black at
 * the top. Under additive blending black adds nothing, so the fade is free — the same trick
 * as the lamp pools in `props.ts`. Fog is off because the point is to be seen from the far
 * side of the region, which is exactly where fog would erase it.
 */
export function buildBeacon(color: number): {
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
} {
  const geometry = new THREE.CylinderGeometry(BEACON_RADIUS * 0.55, BEACON_RADIUS, BEACON_HEIGHT, 10, 1, true);
  geometry.translate(0, BEACON_HEIGHT / 2, 0);
  const tint = new THREE.Color().setHex(color, THREE.SRGBColorSpace);
  const position = geometry.getAttribute('position');
  const colors = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i++) {
    const fade = 1 - position.getY(i) / BEACON_HEIGHT;
    colors[i * 3] = tint.r * fade;
    colors[i * 3 + 1] = tint.g * fade;
    colors[i * 3 + 2] = tint.b * fade;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const material = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    // One side only: from outside, the back faces would add a second, offset copy of the
    // gradient through the front ones and the column came out striped.
    side: THREE.FrontSide,
    fog: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'finish-beacon';
  mesh.renderOrder = 5;
  return { mesh, geometry, material };
}

/**
 * The guide line: a ribbon of quads down the middle of the racing line.
 *
 * V runs along the road in metres divided by `GUIDE_PERIOD`, so one repeat of the chevron
 * texture is the same length of tarmac everywhere no matter how the route's points are
 * spaced — which is what stops the arrows from bunching up through a tight corner where the
 * generator emitted points every half metre.
 */
function buildGuideGeometry(
  path: number[],
  surfaceAt: (x: number, z: number, dirX: number, dirZ: number) => number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const line = resample(path, RESAMPLE_METRES);
  const count = line.length / 2;
  let travelled = 0;
  let emitted = 0;
  let prevX = 0;
  let prevZ = 0;

  for (let i = 0; i < count; i++) {
    const x = line[i * 2];
    const z = line[i * 2 + 1];
    // Tangent from the neighbours, so corners get a mitre rather than a kink.
    const px = line[Math.max(0, i - 1) * 2];
    const pz = line[Math.max(0, i - 1) * 2 + 1];
    const nx = line[Math.min(count - 1, i + 1) * 2];
    const nz = line[Math.min(count - 1, i + 1) * 2 + 1];
    let tx = nx - px;
    let tz = nz - pz;
    const len = Math.hypot(tx, tz);
    if (len < 1e-6) continue;
    tx /= len;
    tz /= len;

    if (emitted > 0) travelled += Math.hypot(x - prevX, z - prevZ);
    prevX = x;
    prevZ = z;

    // Left/right of the direction of travel. Both edges take the *centreline's* height, the
    // same rule the carriageway itself is drawn by — sampling at each edge instead would
    // bank the ribbon on any cross-slope while the road under it stayed level, and one edge
    // would sink into the tarmac.
    const ox = (-tz * GUIDE_WIDTH) / 2;
    const oz = (tx * GUIDE_WIDTH) / 2;
    const y = surfaceAt(x, z, tx, tz) + GUIDE_Y;
    positions.push(x + ox, y, z + oz, x - ox, y, z - oz);
    const v = travelled / GUIDE_PERIOD;
    uvs.push(0, v, 1, v);

    if (emitted > 0) {
      const a = (emitted - 1) * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    emitted++;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

/**
 * Walks a polyline emitting a point at least every `step` metres.
 *
 * Every original vertex is kept — the corners are where the route actually turns — and the
 * long chords between them are filled in. Without this the ribbon is only in contact with
 * the ground where the route happens to have a waypoint.
 */
function resample(path: number[], step: number): number[] {
  const out: number[] = [];
  const count = path.length / 2;
  if (count === 0) return out;
  out.push(path[0], path[1]);
  for (let i = 1; i < count; i++) {
    const ax = path[(i - 1) * 2];
    const az = path[(i - 1) * 2 + 1];
    const bx = path[i * 2];
    const bz = path[i * 2 + 1];
    const length = Math.hypot(bx - ax, bz - az);
    const pieces = Math.max(1, Math.ceil(length / step));
    for (let k = 1; k <= pieces; k++) {
      const t = k / pieces;
      out.push(ax + (bx - ax) * t, az + (bz - az) * t);
    }
  }
  return out;
}

/** A column of arrowheads, pointing along −V, tiled down the guide line. */
function makeChevronTexture(): THREE.Texture {
  const w = 64;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  // A dim bed the whole length, so the route reads as a continuous line from a distance and
  // resolves into arrows as you get closer.
  ctx.fillStyle = 'rgba(255, 190, 60, 0.28)';
  ctx.fillRect(w * 0.18, 0, w * 0.64, h);

  ctx.fillStyle = 'rgba(255, 226, 130, 0.95)';
  for (const top of [0.06, 0.56]) {
    const y = top * h;
    ctx.beginPath();
    ctx.moveTo(w * 0.5, y);
    ctx.lineTo(w * 0.9, y + h * 0.22);
    ctx.lineTo(w * 0.9, y + h * 0.34);
    ctx.lineTo(w * 0.5, y + h * 0.12);
    ctx.lineTo(w * 0.1, y + h * 0.34);
    ctx.lineTo(w * 0.1, y + h * 0.22);
    ctx.closePath();
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/**
 * Chequered flag pattern, generated rather than shipped as an image, and repeated to suit
 * the surface it goes on so a square is `CHEQUER_METRES` across on any street.
 */
function makeCheckerTexture(widthMetres: number, heightMetres: number): THREE.Texture {
  const size = 64;
  const squares = 8;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const step = size / squares;
  for (let y = 0; y < squares; y++) {
    for (let x = 0; x < squares; x++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? '#ffffff' : '#161616';
      ctx.fillRect(x * step, y * step, step, step);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Whole squares in each direction, so the pattern never ends on a sliver at the kerb.
  const across = Math.max(1, Math.round(widthMetres / CHEQUER_METRES));
  const down = Math.max(1, Math.round(heightMetres / CHEQUER_METRES));
  texture.repeat.set(across / squares, down / squares);
  texture.magFilter = THREE.NearestFilter;
  return texture;
}

/** Spacing of the walk along the route, well under what the steepest ramp climbs a metre in. */
const ROUTE_WALK_STEP = 2;

/**
 * The height the route is at, walked from its start the way a car would drive it, and a way to
 * ask what that height was at the nearest point of the walk.
 */
function routeHeights(path: number[], terrain: Heightfield, decks: DeckIndex): (x: number, z: number) => number {
  const line = resample(path, ROUTE_WALK_STEP);
  const heights: number[] = [];
  let y: number | undefined;
  for (let i = 0; i < line.length; i += 2) {
    const x = line[i];
    const z = line[i + 1];
    const ground = terrain.heightAt(x, z);
    y = decks.sample(x, z, y ?? ground)?.y ?? ground;
    heights.push(y);
  }
  const CELL = 16;
  const grid = new Map<number, number[]>();
  for (let k = 0; k < heights.length; k++) {
    const key = Math.floor(line[k * 2] / CELL) * 100003 + Math.floor(line[k * 2 + 1] / CELL);
    const bucket = grid.get(key);
    if (bucket) bucket.push(k);
    else grid.set(key, [k]);
  }
  return (x, z) => {
    const cx = Math.floor(x / CELL);
    const cz = Math.floor(z / CELL);
    let best = -1;
    let bestD = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (const k of grid.get((cx + dx) * 100003 + cz + dz) ?? []) {
          const d = (line[k * 2] - x) ** 2 + (line[k * 2 + 1] - z) ** 2;
          if (d < bestD) {
            bestD = d;
            best = k;
          }
        }
      }
    }
    return best >= 0 ? heights[best] : terrain.heightAt(x, z);
  };
}
